import { useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

// Mirrors app/src/state/session.ts. CrewMember shape is identical so routing
// logic ported from native works unchanged.
export type CrewMember = {
  id: string;
  name: string;
  display_name: string;
  aliases: string[];
  share_location: boolean;
};

export function useSession() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  return { session, loading };
}

export function useGroupCrew(groupId: string | null) {
  const [crew, setCrew] = useState<CrewMember[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!groupId) {
      setCrew(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('group_members')
        .select(
          'user_id, display_name_override, aliases, share_location, users!inner(id, name, display_name)'
        )
        .eq('group_id', groupId);
      if (cancelled) return;
      if (error) {
        setError(error.message);
        setCrew([]);
        return;
      }
      const list: CrewMember[] = (data ?? []).map((row: any) => ({
        id: row.users.id,
        name: row.users.name,
        display_name: row.display_name_override ?? row.users.display_name,
        aliases: row.aliases ?? [],
        share_location: row.share_location ?? true,
      }));
      list.sort((a, b) => a.display_name.localeCompare(b.display_name));
      setCrew(list);
    })();
    return () => {
      cancelled = true;
    };
  }, [groupId]);

  return { crew, error };
}
